import { useFundSummary } from "../lib/hooks";
import ssnLogo from "../images/ssn-logo.png";

function fmt(n: number | undefined | null, prefix = "") {
  if (n === undefined || n === null || isNaN(n)) return "—";
  if (n >= 1_000_000) return `${prefix}${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${prefix}${(n / 1000).toFixed(1)}K`;
  return `${prefix}${n.toLocaleString()}`;
}

export default function HeroSection() {
  const { data, loading } = useFundSummary();

  const cards = data ? [
    { value: data?.total_units !== undefined && data?.total_units !== null ? `${data.total_units}` : "—", label: "TOTAL UNITS",  style: "gold" },
    { value: data?.unit_price_today !== undefined && data?.unit_price_today !== null ? `~$${data.unit_price_today.toLocaleString()}` : "—", label: "UNIT PRICE TODAY", style: "teal" },
    { value: data?.investor_count !== undefined ? String(data.investor_count) : "—", label: "INVESTORS", style: "default" },
    { value: data?.total_invested !== undefined ? fmt(data.total_invested, "$") : "—", label: "TOTAL INVESTED", style: "teal-border" },
    { value: data?.management_fee_pct !== undefined ? `${data.management_fee_pct}%` : "—", label: "MANAGEMENT FEE", style: "dark" },
    { value: data?.operational_cost_pct !== undefined ? `${data.operational_cost_pct}%` : "—", label: "OPERATIONAL COST", sublabel: "↑ ULTRA LOW", style: "dark-gold" },
  ] : Array(6).fill({ value: "—", label: "" });

  return (
    <section style={S.hero}>
      <style>{`
        @media (max-width: 768px) {
          .hero-cards-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 8px !important; }
          .hero-content { padding: 40px 16px 60px !important; gap: 24px !important; }
          .hero-logo-img { width: 80px !important; height: 80px !important; }
        }
        @media (max-width: 480px) {
          .hero-cards-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
      <div style={S.overlay} />
      <div style={S.content} className="hero-content">
        <div style={S.growthWrap}>
          {loading ? (
            <div style={S.growthSkeleton} />
          ) : (
            <>
              <div style={S.growthNum}>
                <span style={S.growthBig}>{data?.fund_growth_pct ? `${data.fund_growth_pct.toFixed(0)}%` : "70%"}</span>
                <span style={{ ...S.growthTime, fontSize: '3.00rem', fontWeight: 'bold' }}>&lt;1 yr</span>
              </div>
              <div style={S.growthLabel}>SHIVSENA FUND GROWTH</div>
            </>
          )}
        </div>

        <div style={S.cardsGrid} className="hero-cards-grid">
          {cards.map((c, i) => (
            <div key={i} style={{ ...S.card, ...cardStyle(c.style) }}>
              {"sublabel" in c && c.sublabel && (
                <div style={S.sublabel}>{c.sublabel}</div>
              )}
              <div style={{ ...S.cardValue, ...valueColor(c.style) }}>
                {loading ? "—" : c.value}
              </div>
              <div style={S.cardLabel}>{c.label}</div>
            </div>
          ))}
        </div>

        <div style={S.heroBottom}>
          <div style={S.heroLogoImg} className="hero-logo-img">
            <img src={ssnLogo} alt="SSN" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          </div>
          <h1 style={S.taglineH1}>Grow Together.</h1>
          <h1 style={S.taglineH1Teal}>Invest Together.</h1>
          <p style={S.taglineP}>
            A private, invite-only investment community built on trust,
            transparency, and long-term thinking.
          </p>
        </div>
      </div>
    </section>
  );
}

function cardStyle(style: string): React.CSSProperties {
  switch (style) {
    case "teal":        return { borderColor: "rgba(29,158,117,0.5)", background: "rgba(29,158,117,0.08)" };
    case "teal-border": return { borderColor: "rgba(29,158,117,0.7)", background: "rgba(9,40,30,0.6)" };
    case "dark":        return { background: "rgba(20,12,4,0.85)", borderColor: "rgba(201,149,42,0.5)" };
    case "dark-gold":   return { background: "rgba(20,12,4,0.85)", borderColor: "rgba(201,149,42,0.5)" };
    case "gold":        return { borderColor: "rgba(201,149,42,0.4)", background: "rgba(201,149,42,0.06)" };
    default:            return {};
  }
}

function valueColor(style: string): React.CSSProperties {
  switch (style) {
    case "gold":        return { color: "var(--gold-light)" };
    case "dark-gold":   return { color: "var(--gold-light)" };
    case "teal":        return { color: "var(--teal-light)" };
    case "teal-border": return { color: "#fff" };
    default:            return { color: "#fff" };
  }
}

const S: Record<string, React.CSSProperties> = {
  hero: {
    position: "relative",
    minHeight: "100vh",
    background: `
      linear-gradient(180deg, rgba(10,15,14,0.55) 0%, rgba(10,15,14,0.72) 60%, rgba(10,15,14,0.92) 100%),
      url('https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1600&q=80') center/cover no-repeat
    `,
    display: "flex", alignItems: "center", justifyContent: "center",
    paddingTop: "var(--nav-h)",
  },
  overlay: { position: "absolute", inset: 0, background: "rgba(0,0,0,0.18)", pointerEvents: "none" },
  content: {
    position: "relative", zIndex: 1,
    maxWidth: 840, width: "100%", margin: "0 auto",
    padding: "60px 24px 80px",
    display: "flex", flexDirection: "column", alignItems: "center", gap: 32,
    boxSizing: "border-box",
  },
  growthWrap: { textAlign: "center" },
  growthSkeleton: { width: 280, height: 100, borderRadius: 8, background: "rgba(255,255,255,0.06)" },
  growthNum: { display: "flex", alignItems: "flex-start", justifyContent: "center", lineHeight: 1 },
  growthBig: {
    fontFamily: "var(--font-display)", fontSize: "clamp(72px,14vw,140px)",
    fontWeight: 700, color: "var(--teal-light)", letterSpacing: "-0.02em",
  },
  growthPct: {
    fontFamily: "var(--font-display)", fontSize: "clamp(40px,8vw,80px)",
    fontWeight: 600, color: "var(--teal-light)", marginTop: "0.15em",
  },
  growthTime: {
    fontFamily: "var(--font-display)", fontSize: "clamp(22px,4vw,48px)",
    fontWeight: 400, color: "var(--text-primary)", marginTop: "0.5em", marginLeft: "0.2em",
  },
  growthLabel: {
    fontSize: 11, letterSpacing: "0.2em", color: "var(--text-secondary)",
    textAlign: "center", marginTop: 4,
  },
  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 12, width: "100%",
  },
  card: {
    padding: "16px 12px",
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: "var(--radius-sm)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    background: "rgba(255,255,255,0.05)",
    textAlign: "center",
    minHeight: 80,
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
    boxSizing: "border-box",
  },
  cardValue: { fontSize: "clamp(16px,3vw,30px)", fontWeight: 700, letterSpacing: "-0.01em" },
  cardLabel: { fontSize: "clamp(8px,1.5vw,10px)", letterSpacing: "0.15em", color: "var(--text-muted)", marginTop: 2 },
  sublabel: { fontSize: 9, letterSpacing: "0.12em", color: "var(--gold-light)", marginBottom: 2 },
  heroBottom: {
    marginTop: "auto",
    marginBottom: "auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    width: "100%",
  },
  heroLogoImg: {
    width: 120,
    height: 120,
    borderRadius: "50%",
    overflow: "hidden",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 4,
    margin: "0 auto 16px",
    boxShadow: "0 0 25px rgba(82, 183, 136, 0.4), 0 4px 10px rgba(0,0,0,0.3)",
    border: "2px solid var(--accent)",
  },
  taglineH1: {
    fontFamily: "var(--font-display)", fontSize: "clamp(24px,5vw,52px)",
    fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.1,
  },
  taglineH1Teal: {
    fontFamily: "var(--font-display)", fontSize: "clamp(24px,5vw,52px)",
    fontWeight: 700, color: "var(--teal-light)", lineHeight: 1.1, marginBottom: 16,
  },
  taglineP: {
    fontSize: "clamp(13px,2vw,15px)", color: "var(--text-secondary)", maxWidth: 520, margin: "0 auto", lineHeight: 1.7,
  },
};
